if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        /* Dots mobile — générer dynamiquement à partir des slides visibles */
        const dotsContainer = this.querySelector('[id^="MediaDots"]');
        if (dotsContainer) {
          const visibleSlides = this.elements.viewer.querySelectorAll('.slider__slide:not([style*="display: none"])');
          visibleSlides.forEach((slide, i) => {
            const dot = document.createElement('button');
            dot.className = 'product__media-dot' + (i === 0 ? ' is-active' : '');
            dot.setAttribute('data-dot-index', i + 1);
            dot.setAttribute('aria-label', 'Image ' + (i + 1));
            dot.addEventListener('click', () => {
              slide.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
            });
            dotsContainer.appendChild(dot);
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        /* Écouter slideChanged pour les dots même sans thumbnails */
        if (!this.elements.thumbnails) {
          this.elements.viewer.addEventListener('slideChanged', debounce((event) => {
            this.updateDots(event.detail.currentElement);
          }, 500));
        }

        /* Masquer les 3 points du lecteur vidéo */
        const observer = new MutationObserver(() => {
          this.querySelectorAll('video').forEach(v => {
            if (!v.hasAttribute('controlslist')) {
              v.setAttribute('controlsList', 'nodownload noplaybackrate');
            }
          });
        });
        observer.observe(this, { childList: true, subtree: true });

        /* Au chargement : scroller vers l'image principale du produit (pas la variante) */
        const featuredSlide = this.elements.viewer.querySelector('[data-product-featured="true"]');
        if (featuredSlide) {
          [150, 400, 800].forEach(delay => {
            window.setTimeout(() => {
              featuredSlide.scrollIntoView({ inline: 'start', block: 'nearest', behavior: 'auto' });
              this.elements.viewer.querySelectorAll('[data-media-id]').forEach(el => el.classList.remove('is-active'));
              featuredSlide.classList.add('is-active');
              this.updateDots(featuredSlide);
              /* Mettre à jour la thumbnail active sur desktop */
              if (this.elements.thumbnails) {
                const mediaId = featuredSlide.dataset.mediaId;
                const thumb = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                if (thumb) this.setActiveThumbnail(thumb);
              }
            }, delay);
          });
        }

        /* Écouter le clic sur les posters vidéo pour passer en 9:16 */
        this.querySelectorAll('.deferred-media__poster').forEach(poster => {
            poster.addEventListener('click', () => {
              const container = poster.closest('.product-media-container');
              if (container && (container.classList.contains('media-type-video') || container.classList.contains('media-type-external_video'))) {
                container.classList.add('is-playing');
                /* Écouter la fin de la vidéo */
                const checkVideo = (attempts) => {
                  attempts = attempts || 0;
                  const video = container.querySelector('video');
                  if (video) {
                    video.addEventListener('ended', () => {
                      container.classList.remove('is-playing');
                      /* Remettre le deferred-media dans son état initial */
                      const deferredEl = container.querySelector('.deferred-media');
                      if (deferredEl) {
                        video.remove();
                        deferredEl.removeAttribute('loaded');
                      }
                    });
                  } else if (attempts < 20) {
                    setTimeout(() => checkVideo(attempts + 1), 300);
                  }
                };
                checkVideo(0);
              }
            });
          });
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        if (thumbnail) this.setActiveThumbnail(thumbnail);

        /* Mettre à jour les dots mobile */
        this.updateDots(event.detail.currentElement);
      }

      updateDots(activeSlide) {
        const dotsContainer = this.querySelector('[id^="MediaDots"]');
        if (!dotsContainer) return;
        const slides = Array.from(this.elements.viewer.querySelectorAll('.slider__slide:not([style*="display: none"])'));
        const slideEl = activeSlide.closest('.slider__slide') || activeSlide;
        const index = slides.indexOf(slideEl);
        const dots = dotsContainer.querySelectorAll('.product__media-dot');
        dots.forEach((dot, i) => {
          dot.classList.toggle('is-active', i === index);
        });

      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          /* Ne pas déplacer — juste réinitialiser le slider */
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          /* Scroller vers l'image de la variante */
          if (activeMedia) {
            activeMedia.scrollIntoView({ inline: 'center', behavior: 'smooth', block: 'nearest' });
          }
          /* Mettre à jour les dots */
          this.updateDots(activeMedia);
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();

        /* Retirer is-playing de tous les containers */
        this.querySelectorAll('.product-media-container.is-playing').forEach(el => el.classList.remove('is-playing'));

        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) {
          deferredMedia.loadContent(false);

          /* Ajouter is-playing pour passer en 9:16 */
          const container = activeItem.querySelector('.product-media-container');
          if (container) {
            container.classList.add('is-playing');

            /* Écouter la fin de la vidéo pour revenir en carré */
            const checkVideo = () => {
              const video = deferredMedia.querySelector('video');
              if (video) {
                video.addEventListener('ended', () => {
                  container.classList.remove('is-playing');
                });
                video.addEventListener('pause', () => {
                  if (video.ended) container.classList.remove('is-playing');
                });
              } else {
                setTimeout(checkVideo, 200);
              }
            };
            checkVideo();
          }
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
